/**
 * Operation Deduplication Tracker
 * Extracted from WebSocketSingleton.ts
 * Tracks processed operations to prevent double-application
 */

import { log } from "@/shared/utils/logger";

export class OperationDeduplicationTracker {
  private processedOperations = new Set<string>();
  private maxTrackedOperations: number;

  constructor(maxTrackedOperations: number = 100) {
    this.maxTrackedOperations = maxTrackedOperations;
  }

  /**
   * Track an operation as processed to prevent double-application
   * Uses session context to allow proper multi-browser sync
   */
  trackOperation(operationId: string, sessionId: string): void {
    if (!operationId || !sessionId) {
      log.warn(
        "OperationDeduplicationTracker",
        "Missing operationId or sessionId for tracking"
      );
      return;
    }

    // Create a composite key that includes both operation ID and session ID
    // This ensures we only skip operations from our own session
    const trackingKey = `${sessionId}:${operationId}`;

    log.debug(
      "OperationDeduplicationTracker",
      "Tracking operation as processed:",
      {
        operationId,
        sessionId,
        trackingKey,
      }
    );

    this.processedOperations.add(trackingKey);

    // Clean up old operation IDs to prevent memory leak
    if (this.processedOperations.size > this.maxTrackedOperations) {
      const ops = Array.from(this.processedOperations);
      this.processedOperations = new Set(ops.slice(-this.maxTrackedOperations));
      log.debug(
        "OperationDeduplicationTracker",
        `Cleaned up old operations, keeping last ${this.maxTrackedOperations}`
      );
    }
  }

  /**
   * Check if an operation has already been processed by a specific session
   */
  isOperationProcessed(operationId: string, sessionId: string): boolean {
    if (!operationId || !sessionId) {
      log.warn(
        "OperationDeduplicationTracker",
        "Missing operationId or sessionId for checking"
      );
      return false;
    }

    // Check with the composite key
    const trackingKey = `${sessionId}:${operationId}`;
    const isProcessed = this.processedOperations.has(trackingKey);

    if (isProcessed) {
      log.debug(
        "OperationDeduplicationTracker",
        "Operation already processed by this session:",
        {
          operationId,
          sessionId,
          trackingKey,
        }
      );
    }

    return isProcessed;
  }

  /**
   * Clear all processed operations (for cleanup)
   */
  clear(): void {
    this.processedOperations.clear();
    log.debug("OperationDeduplicationTracker", "Cleared all tracked operations");
  }

  /**
   * Get count of tracked operations
   */
  getTrackedCount(): number {
    return this.processedOperations.size;
  }

  /**
   * Check if any operations are being tracked
   */
  hasTrackedOperations(): boolean {
    return this.processedOperations.size > 0;
  }
}
